import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'
import { mdiAlert, mdiCheck, mdiPlus, mdiTrashCanOutline } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { useUpdateUser, useDeleteUser } from '../../api/queries'
import { useSetUserPropertyValue } from '../../hooks/useQueries'
import { apiClient } from '../../api/client'

export interface EstablishmentUser {
  uid: string
  name?: string
  // Present on every row from the parent's users-list fetch; not read by
  // this component (it only ever receives already-filtered establishments)
  // but kept here so the route can filter `children` into this narrower
  // type without a separate cast.
  type?: string
  properties?: Record<string, unknown>
  // Not yet returned by GET /users (see queries.ts User.flags) — read
  // defensively so the badge below activates once a later slice exposes it.
  flags?: string[]
}

interface EstablishmentsSectionProps {
  org: string
  companyUid: string
  establishments: EstablishmentUser[]
}

// Small click-to-edit text field, shared by name / address / phone below.
function InlineEditField({
  value,
  placeholder,
  emptyLabel,
  onSave,
  testId,
}: {
  value: string
  placeholder: string
  emptyLabel: string
  onSave: (value: string) => Promise<unknown>
  testId: string
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!editing) setDraft(value)
  }, [value, editing])

  const save = async () => {
    setSaving(true)
    setError('')
    try {
      await onSave(draft.trim())
      setEditing(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  const cancel = () => {
    setError('')
    setDraft(value)
    setEditing(false)
  }

  if (editing) {
    return (
      <div>
        <div className="flex items-center gap-1">
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); void save() }
              else if (e.key === 'Escape') { e.preventDefault(); cancel() }
            }}
            autoFocus
            disabled={saving}
            placeholder={placeholder}
            className={`px-2 py-1 border ${error ? 'border-destructive' : 'border-border'} rounded-md text-sm bg-background text-foreground w-full`}
            data-testid={`${testId}-input`}
          />
          <Button type="button" size="sm" onClick={() => { void save() }} disabled={saving} className="px-2 py-1 text-sm" data-testid={`${testId}-save`}>
            {saving ? '...' : '✓'}
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={cancel} disabled={saving} className="px-2 py-1 text-sm" data-testid={`${testId}-cancel`}>
            ✕
          </Button>
        </div>
        {error && <p className="text-xs text-destructive mt-1" data-testid={`${testId}-error`}>{error}</p>}
      </div>
    )
  }

  return (
    <span
      role="button"
      tabIndex={0}
      className="inline-block text-sm text-foreground cursor-pointer hover:bg-muted/50 px-1.5 py-0.5 rounded transition-colors"
      onClick={() => setEditing(true)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setEditing(true) } }}
      data-testid={`${testId}-value`}
    >
      {value || <span className="text-muted-foreground italic">{emptyLabel}</span>}
    </span>
  )
}

function EstablishmentRow({
  org,
  establishment,
  onRequestDelete,
}: {
  org: string
  establishment: EstablishmentUser
  onRequestDelete: (uid: string) => void
}) {
  const { t } = useTranslation('users')
  const queryClient = useQueryClient()
  const updateUserMutation = useUpdateUser(org, establishment.uid)
  const setPropertyValueMutation = useSetUserPropertyValue(org, establishment.uid)
  const [confirmedLocally, setConfirmedLocally] = useState(false)

  // The row's `address`/`phone` are read from the parent's parent_uid-scoped
  // users list (embedded `properties`, not a per-row fetch — see
  // EstablishmentsSection). useSetUserPropertyValue only invalidates
  // ['userProperties', ...] / ['user', ...], not that list query, so an
  // inline edit would otherwise keep showing the stale value here.
  const savePropertyValue = async (propertyCode: string, value: string) => {
    await setPropertyValueMutation.mutateAsync({ propertyCode, value })
    queryClient.invalidateQueries({ queryKey: ['users', org] })
  }

  const address = String(establishment.properties?.address_raw ?? '')
  const phone = String(establishment.properties?.phone ?? '')
  const toApprove = !confirmedLocally && (establishment.flags?.includes('to_approve') ?? false)

  return (
    <div className="flex flex-col gap-1 py-3" data-testid={`establishment-row-${establishment.uid}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <InlineEditField
            value={establishment.name || ''}
            placeholder={t('establishments.name_placeholder')}
            emptyLabel={t('establishments.name_empty')}
            onSave={(v) => updateUserMutation.mutateAsync({ name: v })}
            testId={`establishment-name-${establishment.uid}`}
          />
          {toApprove && (
            <Badge variant="warning" data-testid={`establishment-to-approve-${establishment.uid}`}>
              <AppIcon path={mdiAlert} size="xs" className="mr-1" />
              {t('establishments.to_approve')}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-1">
          {toApprove && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              aria-label={t('establishments.confirm')}
              title={t('establishments.confirm')}
              onClick={() => setConfirmedLocally(true)}
              data-testid={`establishment-confirm-${establishment.uid}`}
            >
              <AppIcon path={mdiCheck} size="sm" />
            </Button>
          )}
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label={t('common:actions.delete')}
            title={t('common:actions.delete')}
            className="text-destructive hover:text-destructive/80"
            onClick={() => onRequestDelete(establishment.uid)}
            data-testid={`establishment-delete-${establishment.uid}`}
          >
            <AppIcon path={mdiTrashCanOutline} size="sm" />
          </Button>
        </div>
      </div>
      <div className="flex items-center gap-4 text-sm text-muted-foreground">
        <div>
          <span className="mr-1">{t('establishments.address_label')}:</span>
          <InlineEditField
            value={address}
            placeholder={t('establishments.address_placeholder')}
            emptyLabel={t('establishments.address_empty')}
            onSave={(v) => savePropertyValue('address_raw', v)}
            testId={`establishment-address-${establishment.uid}`}
          />
        </div>
        <div>
          <span className="mr-1">{t('establishments.phone_label')}:</span>
          <InlineEditField
            value={phone}
            placeholder={t('establishments.phone_placeholder')}
            emptyLabel={t('establishments.phone_empty')}
            onSave={(v) => savePropertyValue('phone', v)}
            testId={`establishment-phone-${establishment.uid}`}
          />
        </div>
      </div>
    </div>
  )
}

export function EstablishmentsSection({ org, companyUid, establishments }: EstablishmentsSectionProps) {
  const { t } = useTranslation('users')
  const queryClient = useQueryClient()
  const [showAddDialog, setShowAddDialog] = useState(false)
  const [newName, setNewName] = useState('')
  const [newAddress, setNewAddress] = useState('')
  const [newPhone, setNewPhone] = useState('')
  const [addError, setAddError] = useState('')
  const [addPending, setAddPending] = useState(false)
  const [deleteUid, setDeleteUid] = useState<string | null>(null)

  const deleteUserMutation = useDeleteUser(org)

  const openAddDialog = () => {
    setNewName('')
    setNewAddress('')
    setNewPhone('')
    setAddError('')
    setShowAddDialog(true)
  }

  const submitAdd = async () => {
    const trimmedName = newName.trim()
    if (!trimmedName) {
      setAddError(t('establishments.name_required'))
      return
    }
    setAddPending(true)
    setAddError('')
    try {
      const res = await apiClient.POST('/api/v1/organizations/{orgCode}/users', {
        params: { path: { orgCode: org } },
        body: {
          name: trimmedName,
          email: '',
          type: 'establishment',
          parent_user_uid: companyUid,
          properties: {},
        },
      })
      if (res.error || !res.data) {
        const err = res.error as { title?: string; message?: string } | undefined
        throw new Error(err?.title || err?.message || 'Failed to create establishment')
      }
      const newUid = (res.data as { uid: string }).uid
      const trimmedAddress = newAddress.trim()
      const trimmedPhone = newPhone.trim()
      if (trimmedAddress) {
        await apiClient.PUT('/api/v1/organizations/{orgCode}/users/{userUid}/properties/{propertyCode}', {
          params: { path: { orgCode: org, userUid: newUid, propertyCode: 'address_raw' } },
          body: { value: trimmedAddress },
        })
      }
      if (trimmedPhone) {
        await apiClient.PUT('/api/v1/organizations/{orgCode}/users/{userUid}/properties/{propertyCode}', {
          params: { path: { orgCode: org, userUid: newUid, propertyCode: 'phone' } },
          body: { value: trimmedPhone },
        })
      }
      // The list query the parent derives `establishments` from is keyed
      // ['users', org, { parent_uid: companyUid }] — invalidating the
      // ['users', org] prefix (default fuzzy match) covers it.
      queryClient.invalidateQueries({ queryKey: ['users', org] })
      setShowAddDialog(false)
    } catch (err) {
      setAddError(err instanceof Error ? err.message : t('establishments.add_failed'))
    } finally {
      setAddPending(false)
    }
  }

  const addButton = (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={openAddDialog}
      data-testid="add-establishment-button"
    >
      <AppIcon path={mdiPlus} size="sm" className="md:mr-1" />
      <span className="hidden md:inline">{t('establishments.add')}</span>
    </Button>
  )

  return (
    <>
      {establishments.length === 0 ? (
        <div className="flex justify-end">{addButton}</div>
      ) : (
        <div className="bg-card rounded-lg border border-border p-6" data-testid="establishments-section">
          <div className="flex justify-between items-center mb-2">
            <h2 className="text-lg font-semibold text-foreground">{t('establishments.title', { count: establishments.length })}</h2>
            {addButton}
          </div>
          <div className="divide-y divide-border">
            {establishments.map((establishment) => (
              <EstablishmentRow
                key={establishment.uid}
                org={org}
                establishment={establishment}
                onRequestDelete={setDeleteUid}
              />
            ))}
          </div>
        </div>
      )}

      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent data-testid="add-establishment-dialog">
          <DialogHeader>
            <DialogTitle>{t('establishments.add_dialog_title')}</DialogTitle>
            <DialogDescription>{t('establishments.add_dialog_description')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-1">{t('establishments.name_label')}</label>
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                autoFocus
                className="w-full px-3 py-2 border border-border rounded-md text-sm bg-background text-foreground"
                data-testid="add-establishment-name-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-1">{t('establishments.address_label')}</label>
              <input
                type="text"
                value={newAddress}
                onChange={(e) => setNewAddress(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-md text-sm bg-background text-foreground"
                data-testid="add-establishment-address-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-1">{t('establishments.phone_label')}</label>
              <input
                type="text"
                value={newPhone}
                onChange={(e) => setNewPhone(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-md text-sm bg-background text-foreground"
                data-testid="add-establishment-phone-input"
              />
            </div>
            {addError && <p className="text-sm text-destructive" data-testid="add-establishment-error">{addError}</p>}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)} disabled={addPending} data-testid="add-establishment-cancel">
              {t('common:actions.cancel')}
            </Button>
            <Button type="button" onClick={() => { void submitAdd() }} disabled={addPending} data-testid="add-establishment-confirm">
              {addPending ? t('establishments.adding') : t('common:actions.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteUid} onOpenChange={(open) => { if (!open) setDeleteUid(null) }}>
        <AlertDialogContent data-testid="delete-establishment-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('establishments.delete_dialog_title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('establishments.delete_dialog_description')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-establishment">{t('common:actions.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deleteUid) {
                  deleteUserMutation.mutate(deleteUid, {
                    onSettled: () => setDeleteUid(null),
                  })
                }
              }}
              className="bg-destructive hover:bg-destructive/90"
              data-testid="confirm-delete-establishment"
            >
              {t('common:actions.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
